import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MODAL_TYPES } from '@components/modals/constants';
import { useBillingEntities } from '@features/billing-entities';
import type { EligibilityCheckResponse } from '@features/patient-coverages';
import { useSubjectLanguageVariants } from '@features/user';
import { useModal } from '@store/hooks';

import type { BlockReadiness } from '../types/readiness';
import type { EligibilitySource } from '../types/source';
import { isEligibilityCheckDue } from '../utils/eligibilityCheckState';

import { useEligibilityActions } from './useEligibilityActions';

export interface ReadinessActionItem {
  label: string;
  onClick: () => void;
  disabled?: boolean;
}

export interface ReadinessAction {
  primary?: ReadinessActionItem;
  secondary?: ReadinessActionItem;
}

interface Options {
  readiness: BlockReadiness | null;
  hasCoverage: boolean;
  eligibilityData: EligibilityCheckResponse | undefined;
  patientId?: string;
  encounterId?: string;
  patientName?: string;
  runCheck?: () => void;
  canRunCheck?: boolean;
  isRunningCheck?: boolean;
  /**
   * Surface the action is being rendered from. Threaded through to the
   * Stripe invoice modal + detail drawer (when the secondary "Log
   * collection" routes through it) so analytics know whether the click
   * came from popover, edit-appointment slide, or elsewhere.
   */
  source?: EligibilitySource;
}

/**
 * Maps a readiness state to its primary/secondary action for the popover
 * and inline summary.
 *
 *   collect       → Invoice {Subject} (PAYMENT_REQUEST modal) primary +
 *                   Log collection (drawer with form pre-expanded)
 *                   secondary. If billing NPI is missing on the default
 *                   entity, the primary becomes "Finish billing setup"
 *                   and the secondary is suppressed.
 *   blocked       → Add coverage (INSURANCE_EDIT modal)
 *   due/null check → Run eligibility check
 *   !has_coverage → Add coverage
 *   verified /
 *   collected /
 *   attention /
 *   info /
 *   null          → Run eligibility check when coverage is on file.
 *
 * Modal payloads are composed inside `useEligibilityActions` so every
 * eligibility surface (drawer summary, popover, edit-appointment slide,
 * worklist row) routes through the same wiring.
 */
export function useReadinessAction({
  readiness,
  hasCoverage,
  patientId,
  encounterId,
  patientName,
  runCheck,
  canRunCheck = false,
  isRunningCheck = false,
  eligibilityData,
  source,
}: Options): ReadinessAction | null {
  const navigate = useNavigate();
  const { open: openModal } = useModal();
  const { entities: billingEntities } = useBillingEntities({});
  const { singularCap } = useSubjectLanguageVariants();

  const missingBillingNpi = !billingEntities.some(
    (e) => e.is_default && !!e.billing_npi,
  );

  // Trust the BE's pinned encounter when the caller didn't pass one —
  // the server has already worked out which visit this charge belongs
  // to. Same fallback for the appointment id.
  const resolvedEncounterId =
    encounterId ?? eligibilityData?.pinned_encounter_id ?? undefined;
  const resolvedAppointmentId =
    eligibilityData?.pinned_appointment_id ?? undefined;
  const memberObligationCents = readMemberObligationCents(eligibilityData);
  const resolvedSource: EligibilitySource = source ?? 'patient_overview';

  const { openInvoice, openLogCollection } = useEligibilityActions();

  const openAddCoverage = useCallback(() => {
    if (!patientId) return;
    openModal(MODAL_TYPES.INSURANCE_EDIT, { patientId });
  }, [openModal, patientId]);

  const openInvoiceOrFinishSetup = useCallback(() => {
    if (missingBillingNpi) {
      navigate('/practice/billing-settings');
      return;
    }
    openInvoice({
      patientId,
      patientName,
      encounterId: resolvedEncounterId,
      appointmentId: resolvedAppointmentId,
      contextVisitStartsAt: eligibilityData?.pinned_appointment_at,
      memberObligationCents,
      source: resolvedSource,
    });
  }, [
    missingBillingNpi,
    navigate,
    openInvoice,
    patientId,
    patientName,
    resolvedEncounterId,
    resolvedAppointmentId,
    eligibilityData?.pinned_appointment_at,
    memberObligationCents,
    resolvedSource,
  ]);

  const openLogCollectionFromReadiness = useCallback(() => {
    openLogCollection({
      patientId,
      patientName,
      encounterId: resolvedEncounterId,
      appointmentId: resolvedAppointmentId,
      contextVisitStartsAt: eligibilityData?.pinned_appointment_at,
      memberObligationCents,
      source: resolvedSource,
    });
  }, [
    openLogCollection,
    patientId,
    patientName,
    resolvedEncounterId,
    resolvedAppointmentId,
    eligibilityData?.pinned_appointment_at,
    memberObligationCents,
    resolvedSource,
  ]);

  if (eligibilityData?.is_self_pay) {
    // Self-pay surface is informational — no add-coverage or collect actions
    // make sense, and "Mark as insurance instead" lives on the patient page
    // coverages tab, not here (per drawer spec D8).
    return null;
  }

  if (!hasCoverage) {
    return {
      primary: { label: 'Add coverage', onClick: openAddCoverage },
    };
  }

  if ((isEligibilityCheckDue(eligibilityData) || readiness === null) && canRunCheck && runCheck) {
    return {
      primary: {
        label: 'Run eligibility check',
        onClick: runCheck,
        disabled: isRunningCheck,
      },
    };
  }

  switch (readiness) {
    case 'collect':
      return {
        primary: {
          label: missingBillingNpi
            ? 'Finish billing setup'
            : `Invoice ${singularCap}`,
          onClick: openInvoiceOrFinishSetup,
        },
        // Secondary path for orgs that take payment outside Stripe
        // (Square / cash / etc.). Skip it when billing setup is
        // incomplete — finish-setup is the only meaningful action then.
        ...(missingBillingNpi
          ? {}
          : {
              secondary: {
                label: 'Log collection',
                onClick: openLogCollectionFromReadiness,
              },
            }),
      };
    case 'blocked':
      return {
        primary: { label: 'Add coverage', onClick: openAddCoverage },
      };
    case 'verified':
    case 'collected':
    case 'attention':
    case 'info':
    case null:
    default:
      return null;
  }
}

function readMemberObligationCents(
  data: EligibilityCheckResponse | undefined,
): number | null {
  const fin = data?.financials;
  if (!fin) return null;
  const cents = (fin as { member_obligation_cents?: unknown })
    .member_obligation_cents;
  return typeof cents === 'number' ? cents : null;
}
